import * as React from "react";
import { useEffect, useState } from "react";
import { DatePicker } from "../../../../../react-core/Datepicker/DatePicker";
import { MoneyDisplayHelper } from "../../../../../react-core/Helpers/MoneyDisplayHelper";
import { addDays } from "../../../../../react-core/utils/date";
import { CurrencyCode } from "../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { ServiceProposition } from "../../../../../Wrap/Constants/Enums/ServiceProposition";
import { ProductWrapperValuation } from "../../../../../Wrap/Models/Models/Business/AssetUnderManagement/ProductWrapperValuation";
import AdviserSiteTagManager, {
    eventCategoryForGTM,
} from "../../../../Shared/Cookies/ChoicesCookie/AdviserSiteTagManager";
import { LoadingSkeletonType } from "../../../../Shared/LoadingSkeleton/Contents";
import { LoadingSkeletonWrapper } from "../../../../Shared/LoadingSkeleton/LoadingSkeletonWrapper";
import { Table } from "../../../../Shared/Table/Table";
import { getQueryParameters } from "../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { subAccountTypeMap } from "../../Clients/PortfolioDashboard/Components/Performance/Resource/Contents";
import { datePickerOptions } from "../../Clients/Common/Resource/Contents";
import { BusinessManagementAction } from "../Action/BusinessManagementAction";
import { ProductPopup } from "./WrapperSummary/ProductPopup";
import { WrapperSummaryDonut } from "./WrapperSummary/WrapperSummaryDonut";
import { UserStore } from "../../../Stroes/Variable";
import { JsonDateParser } from "../../../../../react-core/Utils/JsonDateParser";

interface IWrapperSummaryProps {
    selectedProposition: ServiceProposition[];
}

export const WrapperSummary = (props: IWrapperSummaryProps) => {
    const [asAt, setAsAt] = useState(addDays(new Date(), -1));
    const [data, setData] = useState<ProductWrapperValuation[]>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [productData, setProductData] = useState(null);
    const [isProductLoading, setIsProductLoading] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    useEffect(() => {
        search(addDays(new Date(), -1));
    }, [props.selectedProposition]);

    useEffect(() => {
        addEvent({
            environment: {
                appType: "web",
                setting: "prod",
                timestamps: JsonDateParser.toJsonDateNoOffset(new Date()).toString(),
            },
            page: {
                pageUrl: window.location.pathname.includes("wrappersummary")
                    ? window.location.pathname
                    : window.location.pathname + "/wrappersummary",
                pageTitle: `${document.title} - business management - wrapper summary`,
            },
            user: {
                loginId: UserStore.getUserInfo()?.ClientId.toString(),
                internal: "yes",
            },
            event: "pageLoaded",
        });
    }, []);

    const search = (asAt) => {
        if (!asAt.isNullOrEmpty) {
            setIsLoading(true);
            BusinessManagementAction.getWrapSummary(
                getQueryParameters()["firm"],
                asAt,
                props.selectedProposition,
                (d) => {
                    setData(d);
                    setIsLoading(false);
                }
            );
        }
    };

    const getProductDetail = (row: ProductWrapperValuation, min: number, max: number) => {
        setIsProductLoading(true);
        BusinessManagementAction.getProductDetailForWrapperSummary(
            getQueryParameters()["firm"],
            asAt,
            row.ProductType,
            props.selectedProposition,
            min,
            max,
            (d) => {
                setProductData(d);
                setIsProductLoading(false);
            }
        );
    };

    let total = 0;
    (data || []).map((item) => {
        total = total + Math.round(item.Value.Amount * 100) / 100;
    });

    const { addEvent } = new AdviserSiteTagManager();
    return (
        <div id={"panel-Wrapper summary"} role="tabpanel" tabIndex={0} aria-labelledby={"tab-Wrapper summary"}>
            <h3>All sub-account statuses will be included in the figures below.</h3>
            <h2 className="mb-16">Wrapper summary</h2>
            <div className="row">
                <div className="col-4">
                    <DatePicker
                        {...datePickerOptions}
                        label="As at"
                        required={"Can't be empty"}
                        selected={asAt}
                        todayButton={null}
                        cssWrapper="control-size-180"
                        lessThan={{
                            value: new Date(),
                            errorMessage: "Date can not be today or future",
                        }}
                        onChange={(v) => {
                            if (v > addDays(new Date(), -1)) {return;}
                            addEvent({
                                event: "wrappersummarytab_interaction",
                                eventParameters: {
                                    eventAction: "interactions",
                                    eventCategory: eventCategoryForGTM.wrapperSummaryTab,
                                    eventLabel: "date widget",
                                },
                            });
                            setAsAt(v);
                            search(v);
                        }}
                    />
                </div>
            </div>

            <LoadingSkeletonWrapper isLoading={isLoading} skeletonType={LoadingSkeletonType.PieChartAndTable}>
                <div className="panel-subtle">
                    <div className="row">
                        <div className="col-12 col-md-3">
                            <WrapperSummaryDonut data={data} />
                        </div>
                        <div className="col-12 col-md-9">
                            {data && (
                                <Table
                                    data={data}
                                    isScrollable={false}
                                    tHeadCssWrapper={"sr-only"}
                                    col={[
                                        {
                                            title: "Product",
                                            key: "Label",
                                            render: (data, row, index) => {
                                                return (
                                                    <>
                                                        <span
                                                            className="product-color"
                                                            aria-hidden={true}
                                                            style={{ borderColor: row.Color }}
                                                        ></span>
                                                        <button
                                                            className="hyperlink"
                                                            onClick={() => {
                                                                addEvent({
                                                                    event: "wrappersummarytab_interaction",
                                                                    eventParameters: {
                                                                        eventAction: "interactions",
                                                                        eventCategory:
                                                                            eventCategoryForGTM.wrapperSummaryTab,
                                                                        eventLabel: subAccountTypeMap[row.ProductType],
                                                                    },
                                                                });
                                                                setSelectedProduct(row);
                                                                getProductDetail(row, 1, 10);
                                                            }}
                                                        >
                                                            {subAccountTypeMap[row.ProductType]}
                                                        </button>
                                                    </>
                                                );
                                            },
                                        },
                                        {
                                            title: "Allocation",
                                            key: "Allocation",
                                            tdCssWrapper: "cell-right",
                                            render: (data, row, index) => {
                                                return `${data}%`;
                                            },
                                        },
                                        {
                                            title: "Value",
                                            key: "Value",
                                            tdCssWrapper: "cell-right text-highlight text-bold",
                                            render: (data, row, index) => {
                                                return MoneyDisplayHelper.getMoneyFormat(row.Value);
                                            },
                                        },
                                    ]}
                                    noDefaultCssWrapper
                                    cssWrapper="accounts-table products-summary-table"
                                    TotalBody={[
                                        { data: "Total" },
                                        { data: "100%", tfTdCssWrapper: "cell-right" },
                                        {
                                            data: MoneyDisplayHelper.getMoneyFormat({
                                                Amount: total,
                                                Currency: CurrencyCode.GBP,
                                            }),
                                            tfTdCssWrapper: "cell-right",
                                        },
                                    ]}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </LoadingSkeletonWrapper>
            <ProductPopup
                data={productData}
                isLoading={isProductLoading}
                onChangePage={(min, max) => getProductDetail(selectedProduct, min, max)}
                productData={selectedProduct}
                isShow={selectedProduct != null}
                onClose={() => {
                    setSelectedProduct(null);
                }}
            />
        </div>
    );
};
